import { useId, useRef, useState, type ReactNode } from 'react'

/**
 * A label that appears on hover, in the product's own voice.
 *
 * The browser's `title` was doing this job and doing it badly: it waits about
 * a second, cannot be styled, never appears for a keyboard user, and on touch
 * does not exist at all. Every one of those matters here, because the tooltip
 * is where the decision buttons keep their words — "Corrigir no sistema" is the
 * whole vocabulary, and a control whose meaning arrives a second late is a
 * control people click without reading.
 *
 * **Rendered into the top layer.** `popover` escapes every ancestor's
 * `overflow`, so a tooltip inside a scrolling board is not clipped by it —
 * which is the failure the native attribute exists to solve and the reason
 * this is not simply an absolutely-positioned div.
 *
 * Shown on focus as well as hover, so somebody tabbing through a conferência
 * gets the same words as somebody with a mouse.
 */
export function Tooltip({
  text,
  children,
  placement = 'top',
}: {
  /**
   * A sentence, or a list: an array is drawn as bullets, one per line. A key
   * legend read as a single line of dots was the case for it.
   */
  text: string | readonly string[]
  children: ReactNode
  /** Which side of the trigger it sits on. Flips nothing: the caller knows. */
  placement?: 'top' | 'bottom'
}) {
  const id = useId()
  const anchor = useRef<HTMLSpanElement>(null)
  const bubble = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState<{ left: number; top: number } | undefined>()

  const show = () => {
    const trigger = anchor.current
    const panel = bubble.current
    if (trigger === null || panel === null) return

    const box = trigger.getBoundingClientRect()
    // Measured after showing, because a popover has no size until it is in the
    // top layer -- reading its width first gives zero and centres it wrongly.
    panel.showPopover()
    const own = panel.getBoundingClientRect()

    setAt({
      left: Math.max(
        4,
        Math.min(box.left + box.width / 2 - own.width / 2, window.innerWidth - own.width - 4),
      ),
      top: placement === 'top' ? box.top - own.height - 6 : box.bottom + 6,
    })
  }

  const hide = () => {
    const panel = bubble.current
    if (panel !== null && panel.matches(':popover-open')) panel.hidePopover()
    setAt(undefined)
  }

  return (
    <span
      ref={anchor}
      className="inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-describedby={id}
    >
      {children}
      <div
        ref={bubble}
        id={id}
        role="tooltip"
        // `manual`, so it closes when we say and not on the first click
        // anywhere -- a light-dismiss tooltip vanishes as the button it
        // describes is pressed.
        popover="manual"
        className="text-tSmall bg-text-strong pointer-events-none m-0 max-w-[240px] rounded-[6px] px-2 py-1 whitespace-normal text-white [overflow-wrap:anywhere]"
        style={{
          position: 'fixed',
          left: at?.left ?? 0,
          top: at?.top ?? 0,
          // Placed before it is measured, so it must not be seen mid-flight.
          visibility: at === undefined ? 'hidden' : 'visible',
          inset: 'auto',
        }}
      >
        {typeof text === 'string' ? (
          text
        ) : (
          <ul className="m-0 flex list-disc flex-col gap-0.5 pl-3">
            {text.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </div>
    </span>
  )
}
